<template>
    <v-form>
        <span class="text-subtitle-1 font-weight-medium">{{ `${$t(`details`)} ${index + 1}` }} </span>
        <v-row dense>
            <v-col cols="12" md="4">
                <airports-search-input
                    v-model="product.departureAirportCode"
                    outlined
                    dense
                    :label="`${$t('flights.departure_airport')}`"
                />
            </v-col>
            <v-col cols="12" md="2">
                <v-text-field
                    v-model="product.departureTerminal"
                    variant="outlined"
                    density="compact"
                    :label="`${$t('flights.departure_terminal')}`"
                    hide-details
                />
            </v-col>
            <v-col cols="12" md="2">
                <date-picker v-model="dateDeparture" :label="$t('departure_date')" outlined density="compact" />
            </v-col>
            <v-col cols="12" md="2">
                <time-field v-model="timeDeparture" outlined density="compact" :label="`${$t('departure_time')}`" />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="4">
                <airports-search-input
                    v-model="product.arrivalAirportCode"
                    outlined
                    dense
                    :label="`${$t('flights.arrival_airport')}`"
                />
            </v-col>
            <v-col cols="12" md="2">
                <v-text-field
                    v-model="product.arrivalTerminal"
                    variant="outlined"
                    density="compact"
                    :label="`${$t('flights.arrival_terminal')}`"
                    hide-details
                />
            </v-col>
            <v-col cols="12" md="2">
                <date-picker v-model="dateArrival" density="compact" :label="$t('arrival_date')" outlined dense />
            </v-col>
            <v-col cols="12" md="2">
                <time-field v-model="timeArrival" outlined density="compact" :label="`${$t('arrival_time')}`" />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="2">
                <v-text-field
                    v-model="product.flightNumber"
                    variant="outlined"
                    density="compact"
                    :label="`${$t('flights.flight_number')}`"
                    hide-details
                />
            </v-col>
            <v-col cols="12" md="2">
                <v-text-field
                    v-model="product.flightClass"
                    variant="outlined"
                    density="compact"
                    :label="`${$t('flights.flight_class')}`"
                    hide-details
                />
            </v-col>
            <v-col cols="12" md="2">
                <v-text-field
                    v-model="product.bookingClass"
                    variant="outlined"
                    density="compact"
                    :label="`${$t('flights.booking_class')}`"
                    hide-details
                />
            </v-col>
            <v-col v-if="product.brandedFare" cols="12" md="2">
                <v-text-field
                    v-model="product.brandedFare.name"
                    variant="outlined"
                    density="compact"
                    :label="`${$t('flights.branded_fare')}`"
                    hide-details
                />
            </v-col>
            <v-col cols="12" md="2">
                <v-checkbox
                    v-model="product.seatsPrebooked"
                    outlined
                    :label="`${$t('flights.seats_prebooked')}`"
                    hide-details
                />
            </v-col>
            <v-col cols="12" md="2">
                <v-checkbox v-model="product.mealIncluded" outlined :label="`${$t('meal_included')}`" hide-details />
            </v-col>
        </v-row>
        <div v-if="product.seats" class="mt-8">
            <v-row v-for="seat in product.seats" :key="seat.travelerRPH" dense align="center" class="mb-2">
                <v-col cols="12" md="2">
                    <v-text-field
                        :model-value="getFullName(tourists[seat.travelerRPH])"
                        variant="outlined"
                        density="compact"
                        :label="`${$t('order_travelers', 1)}`"
                        hide-details
                        readonly
                    />
                </v-col>
                <v-col cols="12" md="2">
                    <v-text-field
                        v-model="seat.seatNumber"
                        variant="outlined"
                        density="compact"
                        :label="`${$t('flights.seat_number')}`"
                        hide-details
                    />
                </v-col>
            </v-row>
        </div>
    </v-form>
</template>

<script lang="ts">
    import {Component, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import AirportsSearchInput from '@/components/snippets/AirportsSearchInput.vue'
    import TimeField from '@/components/snippets/forms/TimeField.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'

    @Component({
        components: {TimeField, AirportsSearchInput, DatePicker},
    })
    export class OfflineServiceFormFlight extends Vue {
        @Prop() productType
        @Prop() index
        @Prop() tourists
        @VModel() product

        airports = []
        departureDateTimeValue = ''
        arrivalDateTimeValue = ''

        mounted() {
            this.departureDateTimeValue = this.product.departureDate || ''
            this.arrivalDateTimeValue = this.product.arrivalDate || ''
        }

        getFullName(tourist) {
            return `${tourist.prefix || ''} ${tourist.firstName || ''} ${tourist.lastName || ''}`
        }

        @Watch('product.seatsPrebooked', {immediate: true})
        onSeatsPrebookedChange() {
            if (this.product.seatsPrebooked) {
                this.product.seats = this.tourists.map((el, i) => ({
                    travelerRPH: i,
                    seatNumber: this.product.seats?.[i]?.seatNumber || '',
                }))
            } else {
                delete this.product.seats
            }
        }

        @Watch('tourists', {immediate: true, deep: true})
        onTouristsChange() {
            if (this.product.seatsPrebooked) {
                this.product.seats = this.tourists.map((el, i) => ({
                    travelerRPH: i,
                    seatNumber: this.product.seats?.[i]?.seatNumber || '',
                }))
            }
        }

        get timeDeparture() {
            return this.departureDateTimeValue?.split('T')?.[1] || ''
        }

        set timeDeparture(val) {
            const dateTime = this.departureDateTimeValue?.split('T') || ['', '']
            dateTime[1] = val
            this.departureDateTimeValue = dateTime.join('T')
            this.product.departureDate = dateTime.join('T')
        }

        get dateDeparture() {
            return this.departureDateTimeValue?.split('T')?.[0] || ''
        }

        set dateDeparture(val) {
            const dateTime = this.departureDateTimeValue?.split('T') || ['', '']
            dateTime[0] = val
            this.departureDateTimeValue = dateTime.join('T')
            this.product.departureDate = dateTime.join('T')
        }

        get timeArrival() {
            return this.arrivalDateTimeValue?.split('T')?.[1] || ''
        }

        set timeArrival(val) {
            const dateTime = this.arrivalDateTimeValue?.split('T') || ['', '']
            dateTime[1] = val
            this.arrivalDateTimeValue = dateTime.join('T')
            this.product.arrivalDate = dateTime.join('T')
        }

        get dateArrival() {
            return this.arrivalDateTimeValue?.split('T')?.[0] || ''
        }

        set dateArrival(val) {
            const dateTime = this.arrivalDateTimeValue?.split('T') || ['', '']
            dateTime[0] = val
            this.arrivalDateTimeValue = dateTime.join('T')
            this.product.arrivalDate = dateTime.join('T')
        }
    }

    export default OfflineServiceFormFlight
</script>
